import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Icon, NameFields } from '../../../../common/components';
import { SimpleHorizontalFormControl } from '../../../../common/simpleComponents';
import moment from 'moment';
import 'moment/locale/en-gb';
import { InfoButton } from '../../../../common/components/index';
import DatePickerDob2 from '../../../../common/components/DatePicker/DatePickerDob2';
import {
  AddressFields,
  CheckboxUnlabeled,
} from '../../../../common/components/ReduxFormField';
import PostcodeList from '../../../../common/components/Postcode/PostcodeList';
import { get } from 'lodash';
import './styles.scss';
import { Row, Col, FormGroup, ControlLabel } from 'react-bootstrap';
import Select from 'react-select';
import getAge from '../../../../helpers/getAge';
import uuid from 'uuid/v4';
import { toastr } from 'react-redux-toastr';

const MIN_AGE = 0;
const MAX_AGE = 120;

const getDefaultTravellerDetails = () => {
  return {
    title: '',
    first_name: '',
    last_name: '',
    dob: '',
    email: '',
    phone1: '',
    phone2: '',
    conditions_covered: true,
    screening: '',
    screening_id: '',
    sanction_id: uuid(),
    sanctions_check: {},
    age: '',
    same_last_name: false,
  };
};

const TravellerAgeFields = ({ input, insured, isChild, childMaxAge, definition }) => {
  useEffect(
    () => {
      const dob = input.value.dob;
      const isValidDate = moment(dob, 'DD/MM/YYYY', true).isValid();
      /**********************************************
       * Update the Age Field with the given date
       *********************************************/
      if (isValidDate) {
        const travellerAge = getAge(dob);
        if (
          travellerAge >= MIN_AGE &&
          travellerAge < MAX_AGE &&
          !isNaN(travellerAge) &&
          isValidDate
        ) {
          const newTraveller = {
            ...input.value,
            age: travellerAge,
          };
          input.onBlur(newTraveller);
        }
      }
    },
    [input],
  );

  return (
    <>
      <Col sm={2}>
        <ControlLabel>Date of birth</ControlLabel>
      </Col>
      <Col sm={2} className="dob-input-col">
        <Field
          name={insured + '.dob'}
          showYear
          simple
          minDate={moment()
            .subtract(isChild ? childMaxAge : 120, 'years')
            .format()}
          maxDate={
            isChild
              ? moment().format()
              : moment()
                  .subtract(18, 'years')
                  .format()
          }
          component={DatePickerDob2}
          label="Date of birth"
          labelSize={6}
          componentSize={6}
        />
      </Col>
      <Col sm={1}>
        <ControlLabel>
          Age <InfoButton content={definition} />
        </ControlLabel>
      </Col>
      <Col sm={2}>
        <Field
          name={insured + '.age'}
          label="Age"
          labelSize={2}
          component={SimpleHorizontalFormControl}
        />
      </Col>
    </>
  );
};
const Title = ({ index, numAdults, handleMoveChildToAdult, isChild, showMove }) => {
  if (!isChild) {
    return <span>Adult {index + 1}</span>;
  }
  if (!showMove) {
    return <span>Child {index + 1}</span>;
  }

  const childPath = `attributes.metadata.children[${index}]`;
  const childNumber = index + 1;
  return (
    <div className={'child-to-adult-select'}>
      <Select
        options={[
          { label: 'Child ' + childNumber, value: childPath },
          { label: 'Adult  ' + numAdults, value: 'mvToAdult' },
        ]}
        onChange={(option) => handleMoveChildToAdult(option.value, childPath, index)}
        defaultValue={{ label: 'Child ' + childNumber, value: childPath }}
      />
    </div>
  );
};

const Travellers = ({
  fields,
  formValues,
  handleMoveChildToAdult,
  formErrors,
  child,
  isRenewal,
  disableAddressFields,
  leadAddress,
  childrenMaxAge,
  label,
  travellerGroup,
  displayGroups,
  currentProduct,
  numAdults,
  onPostcodeChange,
  loggedInUserEmail,
}) => {
  const leadTravellerEmail =  formValues?.[0]?.email ?? '';

  useEffect(
    () => {
      if (leadTravellerEmail && leadTravellerEmail === loggedInUserEmail) {
        toastr.error(
          'Invalid email address',
          "Please do not use your own email address. Enter your customer's email address" +
            ' or leave blank. Please note if your customer takes Smart Luggage they will ' +
            'need an email address.',
          { timeOut: 15000 },
        );
      }
    },
    [leadTravellerEmail],
  );

  useEffect(
    () => {
      // if (!isRenewal) {
      fields.removeAll();
      if (!child) {
        //lead traveller - should always be retained
        fields.push(formValues ? formValues[0] : getDefaultTravellerDetails());
      }
      // }
      if (!child && travellerGroup === 'couple') {
        fields.push(
          formValues && formValues.length >= 2
            ? formValues[1]
            : getDefaultTravellerDetails(),
        );
      }

      if (['group', 'individual'].includes(travellerGroup)) {
        const startIndex = child ? 0 : 1;
        if (formValues && formValues.length >= startIndex) {
          formValues.forEach((formValue, i) => {
            if (i >= startIndex) {
              fields.push(formValue);
            }
          });
        }
      }

      if (travellerGroup === 'family') {
        if (!child && formValues && formValues.length >= 2) {
          fields.push(formValues[1]);
        }
        if (child && formValues && formValues.length >= 0) {
          formValues.forEach((formValue) => {
            fields.push(formValue);
          });
        }
      }
    },
    [travellerGroup],
  );

  const travellerAgeDefinition = get(
    currentProduct,
    'data.attributes.metadata.definitions.b2b.traveller_ages',
    '',
  );
  // Hide if only set to display for specific groups
  if (displayGroups && displayGroups.indexOf(travellerGroup) === -1) {
    return <div />;
  }

  return (
    <div className="travellers">
      {fields.map((insured, index) => {
        const isLeadTraveller = !index && !child && !label;
        return (
          <div
            key={index}
            className={`traveller-row traveller${index + child ? ' child' : ' adult'}`}
          >
            {isLeadTraveller ? (
              <Row>
                <Col sm={12}>
                  <h4>
                    <strong>Lead Traveller</strong>
                  </h4>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col sm={12}>
                  <span
                    className="remove-traveller pull-right"
                    onClick={() => fields.remove(index)}
                  >
                    {travellerGroup !== 'couple' && <Icon name="trash" />}
                  </span>
                  <h4 style={{ paddingTop: '15px' }}>
                    <Title
                      isChild={child}
                      showMove={
                        isRenewal &&
                        get(
                          formErrors,
                          `attributes.metadata.children[${index}].age`,
                          '',
                        ) === 'Child must be under 18.'
                      }
                      index={index}
                      numAdults={numAdults}
                      handleMoveChildToAdult={handleMoveChildToAdult}
                    />
                  </h4>
                </Col>
              </Row>
            )}

            <NameFields
              baseName={insured}
              label="Name"
              labelSize={2}
              fieldComponent={Field}
              fieldSize={6}
              child={child}
              disableOnBlur
            />
            <FormGroup>
              {!isLeadTraveller && (
                <div>
                  <Field
                    name={insured}
                    component={TravellerAgeFields}
                    insured={insured}
                    definition={travellerAgeDefinition}
                    isChild={child}
                    childMaxAge={childrenMaxAge}
                  />
                  <Col sm={4}>
                    <Field
                      name={insured + '.same_last_name'}
                      label="Same Surname Lead Traveller?"
                      component={CheckboxUnlabeled}
                      labelSize={9}
                      options={[{ label: '', value: true }]}
                    />
                  </Col>
                </div>
              )}
            </FormGroup>

            {isLeadTraveller && (
              <div>
                <FormGroup>
                  <Col sm={2}>
                    <ControlLabel>Email address of lead traveller</ControlLabel>
                  </Col>
                  <Col sm={6}>
                    <Field
                      name={insured + '.email'}
                      type="email"
                      placeholder="Optional - enter customer email address"
                      component={SimpleHorizontalFormControl}
                      disabled={isRenewal}
                    />
                  </Col>
                </FormGroup>
                <FormGroup>
                  <Col sm={2}>
                    <ControlLabel>Telephone</ControlLabel>
                  </Col>
                  <Col sm={6}>
                    <Field
                      name={insured + '.phone1'}
                      label="Telephone"
                      placeholder="Optional"
                      component={SimpleHorizontalFormControl}
                    />
                  </Col>
                </FormGroup>
                <FormGroup>
                  <Col sm={2}>
                    <ControlLabel>Mobile</ControlLabel>
                  </Col>
                  <Col sm={6}>
                    <Field
                      name={insured + '.phone2'}
                      placeholder="Optional"
                      component={SimpleHorizontalFormControl}
                    />
                  </Col>
                </FormGroup>
                <FormGroup>
                  <Field
                    name={insured}
                    component={TravellerAgeFields}
                    insured={insured}
                    definition={travellerAgeDefinition}
                    isChild={child}
                    childMaxAge={childrenMaxAge}
                  />
                </FormGroup>
              </div>
            )}

            {isLeadTraveller && leadAddress && (
              <div>
                <PostcodeList
                  name={insured + '.address.postcode'}
                  label="Postcode"
                  labelSize={2}
                  searchSize={2}
                  postcodeFieldSize={5}
                  smFieldSize={7}
                  onPostcodeChange={onPostcodeChange}
                  formName="LeisureTravelForm"
                  value={get(formValues, '[' + index + '].address.postcode', '')}
                  targetField={insured + '.address'}
                />

                <AddressFields
                  baseName={insured + 'address'}
                  label="Address"
                  labelSize={2}
                  smFieldSize={7}
                  disabled={disableAddressFields}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

Travellers.propTypes = {
  fields: PropTypes.object.isRequired,
  formValues: PropTypes.array,
  travellerGroup: PropTypes.string,
  displayGroups: PropTypes.array,
  child: PropTypes.bool,
  label: PropTypes.string,
  buttonLabel: PropTypes.string,
  leadAddress: PropTypes.bool,
  leadContact: PropTypes.bool,
  change: PropTypes.func,
  numberOfTravellers: PropTypes.object,
  currentProduct: PropTypes.object,
};

export default Travellers;
